import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// ----- Helpers (server-side) ---------------------------------------------

async function getOwnerTcId(supabase: any, userId: string): Promise<string> {
  const { data, error } = await supabase
    .from("training_centers")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Cadastre um Centro de Treinamento antes de usar o WhatsApp.");
  return data.id as string;
}

function trimSlash(s: string) {
  return (s || "").replace(/\/+$/, "");
}

async function evolutionFetch(
  baseUrl: string,
  apiKey: string,
  path: string,
  init: RequestInit = {},
): Promise<any> {
  const url = `${trimSlash(baseUrl)}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      apikey: apiKey,
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  let body: any;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { raw: text };
  }
  if (!res.ok) {
    const msg = body?.response?.message || body?.message || body?.error || text || `HTTP ${res.status}`;
    throw new Error(`Evolution API (${res.status}): ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
  }
  return body;
}

async function loadConfig(supabase: any, tcId: string) {
  const { data, error } = await supabase
    .from("whatsapp_configs")
    .select("*")
    .eq("training_center_id", tcId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function onlyDigits(s: string) {
  return (s || "").replace(/\D/g, "");
}

/**
 * Resolve o destino aceito pela Evolution API.
 * Telefone BR sem DDI (10/11 dígitos): prefixa 55
 */
function resolveDestination(target: { phone?: string | null }): string | null {
  const phone = onlyDigits(target.phone || "");
  if (!phone) return null;
  return phone.length <= 11 ? `55${phone}` : phone;
}

/**
 * Verifica se a instância está com sessão aberta na Evolution API.
 * Se estiver "close"/"connecting", tenta reabrir via /instance/connect e
 * lança erro amigável orientando o usuário a reconectar (ler QR) caso não abra.
 * Também atualiza connection_status no banco quando detectar divergência.
 */
async function ensureInstanceOpen(
  supabase: any,
  tcId: string,
  cfg: { base_url: string; global_api_key: string; instance_name: string },
): Promise<void> {
  let state: string | null = null;
  try {
    const res = await evolutionFetch(
      cfg.base_url,
      cfg.global_api_key,
      `/instance/connectionState/${cfg.instance_name}`,
    );
    state = res?.instance?.state || res?.state || null;
  } catch {
    state = null;
  }
  if (state === "open") return;

  // Tenta reabrir automaticamente
  try {
    await evolutionFetch(
      cfg.base_url,
      cfg.global_api_key,
      `/instance/connect/${cfg.instance_name}`,
      { method: "GET" },
    );
    // Aguarda um instante e revalida
    await new Promise((r) => setTimeout(r, 800));
    const res2 = await evolutionFetch(
      cfg.base_url,
      cfg.global_api_key,
      `/instance/connectionState/${cfg.instance_name}`,
    );
    const state2 = res2?.instance?.state || res2?.state || null;
    if (state2 === "open") return;
  } catch {
    // ignore, cai no erro abaixo
  }

  await supabase
    .from("whatsapp_configs")
    .update({ connection_status: "disconnected", last_status_check: new Date().toISOString() })
    .eq("training_center_id", tcId);

  throw new Error(
    "WhatsApp desconectado na Evolution API (sem sessão ativa). Vá em Configuração e Conexão, clique em Verificar Status e, se necessário, leia o QR Code novamente.",
  );
}

// ----- CONFIG -------------------------------------------------------------

export const getWhatsappConfig = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    return {
      tcId,
      baseUrl: cfg?.base_url ?? "",
      globalApiKey: cfg?.global_api_key ?? "",
      instanceName: cfg?.instance_name ?? "",
      connectionStatus: cfg?.connection_status ?? "disconnected",
      qrCode: cfg?.qr_code ?? null,
      ownerNumber: cfg?.owner_number ?? null,
      deviceName: cfg?.device_name ?? null,
      lastStatusCheck: cfg?.last_status_check ?? null,
      hasConfig: !!cfg,
    };
  });

export const saveWhatsappConfig = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { baseUrl: string; globalApiKey: string; instanceName: string }) => d)
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const baseUrl = trimSlash(data.baseUrl.trim());
    const instanceName = data.instanceName.trim().toLowerCase().replace(/\s+/g, "");
    if (!baseUrl || !data.globalApiKey || !instanceName) {
      throw new Error("Preencha URL, Global API Key e Nome da Instância.");
    }
    const { assertInstanceExclusive } = await import("./whatsapp-instance.server");
    await assertInstanceExclusive(tcId, baseUrl, instanceName);
    const { error } = await context.supabase
      .from("whatsapp_configs")
      .upsert(
        {
          training_center_id: tcId,
          base_url: baseUrl,
          global_api_key: data.globalApiKey,
          instance_name: instanceName,
        },
        { onConflict: "training_center_id" },
      );
    if (error) {
      if ((error as any)?.code === "23505") {
        throw new Error(
          `O nome de instância "${instanceName}" já está em uso por outro centro neste servidor. Escolha um nome exclusivo.`,
        );
      }
      throw error;
    }
    return { ok: true };
  });


// ----- INSTANCE / CONNECTION ---------------------------------------------

export const checkInstanceStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Salve as credenciais antes de verificar status.");
    }
    let status = "disconnected";
    let rawState = "unknown";
    try {
      const res = await evolutionFetch(
        cfg.base_url,
        cfg.global_api_key,
        `/instance/connectionState/${cfg.instance_name}`,
      );
      const state = res?.instance?.state || res?.state;
      if (state) rawState = String(state);
      if (state === "open") status = "connected";
      else if (state === "connecting") status = "connecting";
      else status = "disconnected";
    } catch {
      status = "disconnected";
    }
    // Número e nome do aparelho atualmente vinculados à instância (quando o servidor informa)
    let ownerNumber: string | null = null;
    let deviceName: string | null = null;
    const remote = await findRemoteInstance(cfg);
    if (remote.exists) {
      if (remote.owner) ownerNumber = remote.owner;
      if (remote.deviceName) deviceName = remote.deviceName;
      if (rawState === "unknown" && remote.state) rawState = remote.state;
    }
    const now = new Date().toISOString();
    await context.supabase
      .from("whatsapp_configs")
      .update({
        connection_status: status,
        owner_number: ownerNumber,
        device_name: deviceName,
        last_status_check: now,
      })
      .eq("training_center_id", tcId);
    return { status, rawState, ownerNumber, deviceName, instanceExists: remote.exists, lastStatusCheck: now };
  });

function extractQr(res: any): string | null {
  if (!res) return null;
  const candidates = [
    res?.base64,
    res?.qrcode?.base64,
    res?.qrcode?.code,
    typeof res?.qrcode === "string" ? res.qrcode : null,
    res?.code,
    res?.qr,
    res?.instance?.qrcode?.base64,
    res?.instance?.qrcode,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.length > 20) return c;
  }
  return null;
}

async function findRemoteInstance(
  cfg: any,
): Promise<{ exists: boolean; state?: string; owner?: string | null; deviceName?: string | null }> {
  try {
    const list = await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/fetchInstances`);
    const items: any[] = Array.isArray(list) ? list : list?.instances || [];
    for (const it of items) {
      const name = it?.instance?.instanceName || it?.instanceName || it?.name;
      if (name === cfg.instance_name) {
        const state =
          it?.instance?.state || it?.connectionStatus || it?.status || it?.state || "unknown";
        const jid = it?.instance?.owner || it?.ownerJid || it?.owner || it?.instance?.ownerJid;
        const owner = typeof jid === "string" ? jid.split("@")[0] : null;
        const dName =
          it?.instance?.profileName ||
          it?.instance?.profileName ||
          it?.profileName ||
          it?.instance?.name ||
          it?.name ||
          null;
        const deviceName = typeof dName === "string" ? dName : null;
        return { exists: true, state, owner, deviceName };
      }
    }
  } catch (e) {
    console.log("[whatsapp] fetchInstances failed", (e as any)?.message);
  }
  return { exists: false };
}

export const connectInstance = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Salve as credenciais antes de conectar.");
    }

    console.log("[whatsapp] connectInstance start", cfg.instance_name);

    const remote = await findRemoteInstance(cfg);
    console.log("[whatsapp] remote state", remote);

    if (remote.exists && remote.state === "open") {
      await context.supabase
        .from("whatsapp_configs")
        .update({ connection_status: "connected", qr_code: null })
        .eq("training_center_id", tcId);
      return { qrCode: null, alreadyConnected: true };
    }

    if (!remote.exists) {
      try {
        await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/create`, {
          method: "POST",
          body: JSON.stringify({
            instanceName: cfg.instance_name,
            qrcode: true,
            integration: "WHATSAPP-BAILEYS",
          }),
        });
      } catch (e: any) {
        const msg = e?.message || "";
        if (!/already|exists|in use/i.test(msg)) {
          console.log("[whatsapp] create failed", msg);
          throw new Error(`Falha ao criar instância: ${msg}`);
        }
      }
    }

    // Polling /instance/connect até obter base64 (Evolution v2 às vezes demora 1-2s)
    let qr: string | null = null;
    let lastRes: any = null;
    for (let i = 0; i < 5; i++) {
      try {
        const res = await evolutionFetch(
          cfg.base_url,
          cfg.global_api_key,
          `/instance/connect/${cfg.instance_name}`,
          { method: "GET" },
        );
        lastRes = res;
        qr = extractQr(res);
        if (qr) break;
      } catch (e: any) {
        const msg = e?.message || "";
        console.log("[whatsapp] connect attempt", i, msg);
        if (/already.*connected|already.*open/i.test(msg)) {
          await context.supabase
            .from("whatsapp_configs")
            .update({ connection_status: "connected", qr_code: null })
            .eq("training_center_id", tcId);
          return { qrCode: null, alreadyConnected: true };
        }
        if (i === 4) throw new Error(`Falha ao conectar: ${msg}`);
      }
      await new Promise((r) => setTimeout(r, 1200));
    }

    if (!qr) {
      console.log("[whatsapp] no qr after polling, response keys:", Object.keys(lastRes || {}));
    }

    await context.supabase
      .from("whatsapp_configs")
      .update({ qr_code: qr, connection_status: "connecting" })
      .eq("training_center_id", tcId);
    return { qrCode: qr };
  });

export const fetchQrCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      return { qrCode: null, status: "disconnected" };
    }
    // status check
    let status = "connecting";
    try {
      const s = await evolutionFetch(
        cfg.base_url,
        cfg.global_api_key,
        `/instance/connectionState/${cfg.instance_name}`,
      );
      const state = s?.instance?.state || s?.state;
      if (state === "open") status = "connected";
      else if (state === "connecting") status = "connecting";
      else status = "disconnected";
    } catch {}

    if (status === "connected") {
      await context.supabase
        .from("whatsapp_configs")
        .update({ connection_status: "connected", qr_code: null })
        .eq("training_center_id", tcId);
      return { qrCode: null, status };
    }

    let qr: string | null = null;
    try {
      const res = await evolutionFetch(
        cfg.base_url,
        cfg.global_api_key,
        `/instance/connect/${cfg.instance_name}`,
        { method: "GET" },
      );
      qr = extractQr(res);
    } catch (e: any) {
      console.log("[whatsapp] fetchQrCode error", e?.message);
    }

    await context.supabase
      .from("whatsapp_configs")
      .update({ qr_code: qr, connection_status: status })
      .eq("training_center_id", tcId);
    return { qrCode: qr, status };
  });

export const disconnectInstance = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) return { ok: true };
    const { assertSafeToDestroy } = await import("./whatsapp-instance.server");
    await assertSafeToDestroy(tcId, cfg.base_url, cfg.instance_name);
    try {
      await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/logout/${cfg.instance_name}`, {
        method: "DELETE",
      });
    } catch {}
    try {
      await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/delete/${cfg.instance_name}`, {
        method: "DELETE",
      });
    } catch {}
    await context.supabase
      .from("whatsapp_configs")
      .update({
        connection_status: "disconnected",
        qr_code: null,
        owner_number: null,
        device_name: null,
      })
      .eq("training_center_id", tcId);
    return { ok: true };
  });


const PREVIEW_URL = "https://project--deffebd9-9fd1-4783-be35-669fadd07128-dev.lovable.app";
const PUBLISHED_URL = "https://expressify-designs.lovable.app";

export const setEvolutionWebhook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { target?: "preview" | "published" } = {} as any) => d ?? {})
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Salve as credenciais antes de configurar webhook.");
    }
    const target = data?.target || "published";
    const origin = target === "preview" ? PREVIEW_URL : (process.env.PUBLIC_APP_URL || PUBLISHED_URL);
    const webhookUrl = `${trimSlash(origin)}/api/public/evolution-webhook?tc=${tcId}&secret=${cfg.webhook_secret}`;
    const payload: any = {
      url: webhookUrl,
      enabled: true,
      webhookByEvents: false,
      webhook_by_events: false,
      webhookBase64: true,
      webhook_base64: true,
      events: ["CONNECTION_UPDATE", "QRCODE_UPDATED", "MESSAGES_UPSERT"],
    };
    await evolutionFetch(cfg.base_url, cfg.global_api_key, `/webhook/set/${cfg.instance_name}`, {
      method: "POST",
      body: JSON.stringify({ webhook: payload, ...payload }),
    });
    return { ok: true, webhookUrl, target };
  });

export const getEvolutionWebhook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Salve as credenciais primeiro.");
    }
    try {
      const res = await evolutionFetch(
        cfg.base_url,
        cfg.global_api_key,
        `/webhook/find/${cfg.instance_name}`,
        { method: "GET" },
      );
      const w = res?.webhook || res;
      return {
        url: w?.url || null,
        enabled: w?.enabled ?? null,
        events: w?.events || [],
        webhookBase64: w?.webhookBase64 ?? w?.webhook_base64 ?? null,
        webhookByEvents: w?.webhookByEvents ?? w?.webhook_by_events ?? null,
      };
    } catch (e: any) {
      throw new Error(`Falha ao consultar webhook: ${e?.message || e}`);
    }
  });

export const cleanupOtherInstances = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Salve as credenciais primeiro.");
    }
    // Instâncias usadas por OUTROS centros de treinamento nunca podem ser apagadas.
    const protectedNames = new Set<string>([cfg.instance_name]);
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: others } = await supabaseAdmin
        .from("whatsapp_configs")
        .select("instance_name")
        .not("instance_name", "is", null);
      for (const row of (others as any[]) ?? []) {
        if (row?.instance_name) protectedNames.add(String(row.instance_name));
      }
    } catch (e) {
      console.log("[whatsapp] cleanup: falha ao carregar instâncias protegidas", e);
      throw new Error("Não foi possível validar as instâncias dos outros centros. Tente novamente.");
    }

    const list = await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/fetchInstances`);
    const items: any[] = Array.isArray(list) ? list : list?.instances || [];
    let removed = 0;
    let skipped = 0;
    for (const it of items) {
      const name = it?.instance?.instanceName || it?.instanceName || it?.name;
      if (!name) continue;
      if (protectedNames.has(String(name))) {
        skipped++;
        continue;
      }
      try {
        await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/logout/${name}`, { method: "DELETE" });
      } catch {}
      try {
        await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/delete/${name}`, { method: "DELETE" });
        removed++;
      } catch {}
    }
    return { removed, skipped };
  });

/**
 * Apaga SOMENTE a instância deste centro na Evolution e recria, devolvendo um QR novo.
 */
export const resetInstance = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Salve as credenciais antes de resetar a instância.");
    }
    const { assertSafeToDestroy } = await import("./whatsapp-instance.server");
    await assertSafeToDestroy(tcId, cfg.base_url, cfg.instance_name);

    for (const path of [`/instance/logout/${cfg.instance_name}`, `/instance/delete/${cfg.instance_name}`]) {
      try {
        await evolutionFetch(cfg.base_url, cfg.global_api_key, path, { method: "DELETE" });
      } catch (e: any) {
        console.log("[whatsapp] reset step falhou", path, e?.message);
      }
    }
    await context.supabase
      .from("whatsapp_configs")
      .update({
        connection_status: "disconnected",
        qr_code: null,
        owner_number: null,
        device_name: null,
      })
      .eq("training_center_id", tcId);

    // Aguarda o servidor confirmar que a instância sumiu antes de recriar
    // (evita "sessão fantasma": QR novo que fica girando sem conectar).
    for (let i = 0; i < 8; i++) {
      await new Promise((r) => setTimeout(r, 1200));
      const still = await findRemoteInstance(cfg);
      if (!still.exists) break;
      if (i === 3) {
        try {
          await evolutionFetch(
            cfg.base_url,
            cfg.global_api_key,
            `/instance/delete/${cfg.instance_name}`,
            { method: "DELETE" },
          );
        } catch {}
      }
    }

    try {
      await evolutionFetch(cfg.base_url, cfg.global_api_key, `/instance/create`, {
        method: "POST",
        body: JSON.stringify({
          instanceName: cfg.instance_name,
          qrcode: true,
          integration: "WHATSAPP-BAILEYS",
        }),
      });
    } catch (e: any) {
      const msg = e?.message || "";
      if (!/already|exists|in use/i.test(msg)) {
        throw new Error(`Falha ao recriar instância: ${msg}`);
      }
    }

    let qr: string | null = null;
    let lastError = "";
    for (let i = 0; i < 6; i++) {
      try {
        const res = await evolutionFetch(
          cfg.base_url,
          cfg.global_api_key,
          `/instance/connect/${cfg.instance_name}`,
          { method: "GET" },
        );
        qr = extractQr(res);
        if (qr) break;
      } catch (e: any) {
        lastError = e?.message || "";
        console.log("[whatsapp] reset connect attempt", i, lastError);
      }
      await new Promise((r) => setTimeout(r, 1500));
    }

    await context.supabase
      .from("whatsapp_configs")
      .update({ qr_code: qr, connection_status: qr ? "connecting" : "disconnected" })
      .eq("training_center_id", tcId);

    if (!qr) {
      throw new Error(
        lastError
          ? `Instância resetada, mas o servidor não devolveu o QR: ${lastError}`
          : "Instância resetada, mas o servidor não devolveu o QR. Tente Gerar QR Code novamente em alguns segundos.",
      );
    }
    return { qrCode: qr };
  });

// ----- TEMPLATES ----------------------------------------------------------

export const listTemplates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("whatsapp_message_templates")
      .select("*")
      .eq("training_center_id", tcId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  });

export const saveTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: {
      id?: string;
      name: string;
      message: string;
      trigger_event: string;
      target_audience?: string;
      is_active: boolean;
      stop_condition?: "pago" | "envio_unico" | "max_envios" | "data_limite";
      stop_max_sends?: number | null;
      stop_date?: string | null;
    }) => d,
  )
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    if (!data.name?.trim() || !data.message?.trim()) {
      throw new Error("Nome e mensagem são obrigatórios.");
    }
    const cond = data.stop_condition || "envio_unico";
    const payload: any = {
      training_center_id: tcId,
      name: data.name.trim(),
      message: data.message,
      trigger_event: data.trigger_event || "MANUAL",
      target_audience: data.target_audience || "ALL",
      is_active: data.is_active,
      stop_condition: cond,
      stop_max_sends: cond === "max_envios" ? (data.stop_max_sends ?? null) : null,
      stop_date: cond === "data_limite" ? (data.stop_date ?? null) : null,
    };
    if (data.id) {
      const { error } = await context.supabase
        .from("whatsapp_message_templates")
        .update(payload)
        .eq("id", data.id);
      if (error) throw error;
    } else {
      const { error } = await context.supabase.from("whatsapp_message_templates").insert(payload);
      if (error) throw error;
    }
    return { ok: true };
  });

export const deleteTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase.from("whatsapp_message_templates").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const toggleTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; is_active: boolean }) => d)
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("whatsapp_message_templates")
      .update({ is_active: data.is_active })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// ----- AUTOMATIONS --------------------------------------------------------

export const listAutomations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("whatsapp_automations")
      .select("*, whatsapp_message_templates(name)")
      .eq("training_center_id", tcId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  });

export const saveAutomation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { event_type: string; days: number; template_id: string; is_active: boolean }) => d)
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    if (!data.template_id) throw new Error("Selecione um template.");
    const days = data.event_type === "RENEWAL_DUE" ? 0 : Math.max(1, data.days || 1);
    const { error } = await context.supabase.from("whatsapp_automations").insert({
      training_center_id: tcId,
      event_type: data.event_type,
      days,
      template_id: data.template_id,
      is_active: data.is_active,
    });
    if (error) throw error;
    return { ok: true };
  });

export const deleteAutomation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase.from("whatsapp_automations").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const toggleAutomation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; is_active: boolean }) => d)
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("whatsapp_automations")
      .update({ is_active: data.is_active })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// ----- CONTACTS + BROADCAST ----------------------------------------------

export const listContactsForBroadcast = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: { responsavel?: string; aluno?: string; anoNasc?: string } = {}) => d,
  )
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    let q = context.supabase
      .from("students")
      .select("id, name, birth_date, parent:parents(id, name, phone)")
      .eq("training_center_id", tcId)
      .order("name");
    if (data.aluno) q = q.ilike("name", `%${data.aluno}%`);
    const { data: rows, error } = await q;
    if (error) throw error;
    let list = (rows ?? []).map((r: any) => ({
      id: r.id,
      aluno: r.name,
      anoNasc: r.birth_date ? new Date(r.birth_date).getFullYear() : null,
      responsavel: r.parent?.name ?? "",
      whatsapp: r.parent?.phone ?? "",
      parentId: r.parent?.id ?? null,
    }));
    if (data.responsavel) {
      const t = data.responsavel.toLowerCase();
      list = list.filter((x) => x.responsavel.toLowerCase().includes(t));
    }
    if (data.anoNasc) {
      const y = Number(data.anoNasc);
      list = list.filter((x) => x.anoNasc === y);
    }
    return list;
  });

export const sendWhatsappBroadcast = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: {
      message: string;
      templateId?: string | null;
      recipients: Array<{ name: string; phone?: string | null }>;
    }) => d,
  )
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Configure a Evolution API antes de enviar mensagens.");
    }
    if (!data.message?.trim()) throw new Error("Mensagem vazia.");
    if (!Array.isArray(data.recipients) || data.recipients.length === 0) {
      throw new Error("Selecione ao menos um destinatário.");
    }
    await ensureInstanceOpen(context.supabase, tcId, cfg as any);
    let sent = 0;
    let failed = 0;
    const errors: string[] = [];
    for (const r of data.recipients) {
      const number = resolveDestination(r);
      if (!number) {
        failed++;
        await context.supabase.from("whatsapp_message_logs").insert({
          training_center_id: tcId,
          recipient_phone: r.phone || "",
          recipient_name: r.name,
          message: data.message,
          status: "failed",
          error: "Destino inválido",
          template_id: data.templateId ?? null,
        });
        continue;
      }
      const personalized = data.message.replace(/\{nome\}/gi, r.name || "");
      try {
        await evolutionFetch(cfg.base_url, cfg.global_api_key, `/message/sendText/${cfg.instance_name}`, {
          method: "POST",
          body: JSON.stringify({ number, text: personalized }),
        });
        sent++;
        await context.supabase.from("whatsapp_message_logs").insert({
          training_center_id: tcId,
          recipient_phone: number,
          recipient_name: r.name,
          message: personalized,
          status: "sent",
          template_id: data.templateId ?? null,
        });
      } catch (e: any) {
        failed++;
        errors.push(`${r.name}: ${e?.message || "erro"}`);
        await context.supabase.from("whatsapp_message_logs").insert({
          training_center_id: tcId,
          recipient_phone: number,
          recipient_name: r.name,
          message: personalized,
          status: "failed",
          error: String(e?.message || e),
          template_id: data.templateId ?? null,
        });
      }
    }
    return { sent, failed, errors };
  });

/**
 * Envia várias mensagens em sequência para o MESMO destinatário (uma única chamada).
 * Útil para quebrar texto + chave Pix + BR Code em mensagens separadas, deixando
 * cada bloco isolado para "toque longo → copiar" no WhatsApp.
 */
export const sendWhatsappSequence = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: { recipient: { name: string; phone?: string | null }; messages: string[] }) => d,
  )
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Configure a Evolution API antes de enviar mensagens.");
    }
    const number = resolveDestination(data.recipient);
    if (!number) throw new Error("Destino inválido.");
    const msgs = (data.messages || []).map((m) => (m || "").trim()).filter(Boolean);
    if (msgs.length === 0) throw new Error("Nenhuma mensagem.");
    await ensureInstanceOpen(context.supabase, tcId, cfg as any);


    let sent = 0;
    const errors: string[] = [];
    for (const text of msgs) {
      try {
        await evolutionFetch(cfg.base_url, cfg.global_api_key, `/message/sendText/${cfg.instance_name}`, {
          method: "POST",
          body: JSON.stringify({ number, text }),
        });
        sent++;
        await context.supabase.from("whatsapp_message_logs").insert({
          training_center_id: tcId,
          recipient_phone: number,
          recipient_name: data.recipient.name,
          message: text,
          status: "sent",
        });
        // Pequeno delay para preservar a ordem visual no WhatsApp
        await new Promise((r) => setTimeout(r, 350));
      } catch (e: any) {
        errors.push(e?.message || "erro");
        await context.supabase.from("whatsapp_message_logs").insert({
          training_center_id: tcId,
          recipient_phone: number,
          recipient_name: data.recipient.name,
          message: text,
          status: "failed",
          error: String(e?.message || e),
        });
      }
    }
    return { sent, failed: msgs.length - sent, errors };
  });

/**
 * Envia o PDF do último contrato do aluno para o WhatsApp do responsável
 * via Evolution API (/message/sendMedia). Usa signedUrl temporária do
 * bucket "contracts" — nada de expor a URL fora do WhatsApp.
 */
export const sendContractPdfViaWhatsapp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { studentId: string; caption?: string }) => d)
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Configure a Evolution API antes de enviar mensagens.");
    }

    // Aluno + telefone do responsável + nome
    const { data: s, error: sErr } = await context.supabase
      .from("students")
      .select("id,name,parents(name,phone)")
      .eq("id", data.studentId)
      .eq("training_center_id", tcId)
      .maybeSingle();
    if (sErr) throw sErr;
    if (!s) throw new Error("Aluno não encontrado.");
    const phoneRaw = onlyDigits((s as any).parents?.phone || "");
    if (!phoneRaw) throw new Error("Responsável sem telefone cadastrado.");
    const number = phoneRaw.length <= 11 ? `55${phoneRaw}` : phoneRaw;

    // Contrato mais recente com PDF gerado (prioriza uploaded_contract_url se houver)
    const { data: contracts, error: cErr } = await context.supabase
      .from("contracts")
      .select("id, pdf_url, uploaded_contract_url, created_at")
      .eq("student_id", s.id)
      .order("created_at", { ascending: false })
      .limit(1);
    if (cErr) throw cErr;
    const contract = contracts?.[0];
    const pdfPath = contract?.uploaded_contract_url || contract?.pdf_url;
    if (!contract || !pdfPath) {
      throw new Error("Nenhum contrato com PDF encontrado para este aluno.");
    }

    // Signed URL válida por 1h (Evolution baixa e reenvia para o WhatsApp)
    const { data: signed, error: uErr } = await context.supabase.storage
      .from("contracts")
      .createSignedUrl(pdfPath, 60 * 60);
    if (uErr || !signed?.signedUrl) throw uErr || new Error("Falha ao gerar link do PDF.");

    await ensureInstanceOpen(context.supabase, tcId, cfg as any);

    const fileName = `contrato-${(s.name || "aluno").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
    const caption =
      data.caption?.trim() ||
      `📄 Contrato do(a) ${s.name}. Segue em anexo para sua conferência.`;

    try {
      await evolutionFetch(
        cfg.base_url,
        cfg.global_api_key,
        `/message/sendMedia/${cfg.instance_name}`,
        {
          method: "POST",
          body: JSON.stringify({
            number,
            mediatype: "document",
            mimetype: "application/pdf",
            media: signed.signedUrl,
            fileName,
            caption,
          }),
        },
      );
      await context.supabase.from("whatsapp_message_logs").insert({
        training_center_id: tcId,
        recipient_phone: number,
        recipient_name: (s as any).parents?.name || s.name,
        message: `[PDF] ${fileName} — ${caption}`,
        status: "sent",
      });
      return { ok: true };
    } catch (e: any) {
      await context.supabase.from("whatsapp_message_logs").insert({
        training_center_id: tcId,
        recipient_phone: number,
        recipient_name: (s as any).parents?.name || s.name,
        message: `[PDF] ${fileName}`,
        status: "failed",
        error: String(e?.message || e),
      });
      throw e;
    }
  });

/**
 * Envia um PDF já renderizado no cliente (base64) via WhatsApp para um número
 * arbitrário. Usado pela Súmula do Jogo — permite anexar o PDF completo.
 */
export const sendPdfBase64ViaWhatsapp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { phone: string; base64: string; fileName: string; caption?: string; recipientName?: string }) => d)
  .handler(async ({ context, data }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const cfg = await loadConfig(context.supabase, tcId);
    if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
      throw new Error("Configure a Evolution API antes de enviar mensagens.");
    }
    const phoneRaw = onlyDigits(data.phone);
    if (!phoneRaw) throw new Error("Telefone inválido.");
    const number = phoneRaw.length <= 11 ? `55${phoneRaw}` : phoneRaw;
    if (!data.base64) throw new Error("PDF vazio.");
    const fileName = data.fileName?.endsWith(".pdf") ? data.fileName : `${data.fileName || "documento"}.pdf`;
    const caption = data.caption?.trim() || `📄 ${fileName}`;

    await ensureInstanceOpen(context.supabase, tcId, cfg as any);
    try {
      await evolutionFetch(
        cfg.base_url,
        cfg.global_api_key,
        `/message/sendMedia/${cfg.instance_name}`,
        {
          method: "POST",
          body: JSON.stringify({
            number,
            mediatype: "document",
            mimetype: "application/pdf",
            media: data.base64,
            fileName,
            caption,
          }),
        },
      );
      await context.supabase.from("whatsapp_message_logs").insert({
        training_center_id: tcId,
        recipient_phone: number,
        recipient_name: data.recipientName || "",
        message: `[PDF] ${fileName} — ${caption}`,
        status: "sent",
      });
      return { ok: true };
    } catch (e: any) {
      await context.supabase.from("whatsapp_message_logs").insert({
        training_center_id: tcId,
        recipient_phone: number,
        recipient_name: data.recipientName || "",
        message: `[PDF] ${fileName}`,
        status: "failed",
        error: String(e?.message || e),
      });
      throw e;
    }
  });

export const listWhatsappHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { page?: number; pageSize?: number; from?: string; to?: string; status?: string; query?: string }) => d)
  .handler(async ({ data, context }) => {
    const tcId = await getOwnerTcId(context.supabase, context.userId);
    const page = Math.max(1, data.page || 1);
    const pageSize = Math.min(200, data.pageSize || 50);
    const fromIdx = (page - 1) * pageSize;
    const toIdx = fromIdx + pageSize - 1;

    let q = context.supabase
      .from("whatsapp_message_logs")
      .select("id, sent_at, recipient_name, recipient_phone, message, status, error", { count: "exact" })
      .eq("training_center_id", tcId)
      .order("sent_at", { ascending: false });

    if (data.from) q = q.gte("sent_at", data.from);
    if (data.to) q = q.lte("sent_at", data.to);
    if (data.status && data.status !== "all") q = q.eq("status", data.status);
    if (data.query && data.query.trim()) {
      const t = `%${data.query.trim()}%`;
      q = q.or(`recipient_name.ilike.${t},recipient_phone.ilike.${t}`);
    }

    const { data: rows, error, count } = await q.range(fromIdx, toIdx);
    if (error) throw error;
    return { rows: rows || [], total: count || 0, page, pageSize };
  });




